import { z } from 'zod';
import {
  blockMetaSchema,
  contactPersonSchema,
  createModel,
  currencySchema,
  imageWithUploadSchema,
  legalEntitySchema,
  membersCountSchema,
  partyRoleSchema,
  QueryMany,
  QueryOne,
} from '../../common';
import { nameSchema } from '../../validation';
import { UTagSharedSchema } from '../utag';
import { orgStatusSchema } from './org.enums';
import { orgSettingsSchema } from './org.settings.model';
import { orgFleetSchema } from './org.fleet.model';
import { orgMetaSchema } from './org.meta.model';
import { orgFinancingProfileSchema } from '../financing/financing.profile.model';

/**
 * `Org db model` — unified replacement for the legacy `Business` and
 * `Carrier` collections.
 *
 * Rationale (see `docs/relations-v2/07-collection-unification.md`):
 * the Business / Carrier split today is by role, not by data shape.
 * An Org that's both a customer AND a carrier has to be represented
 * across two collections, which the Hero network model rejects — any
 * party can play any role in any direction. `Org.roles[]` (min 1)
 * captures multi-role membership.
 *
 * Expected indexes (operator-applied):
 * - `{ status: 1 }`
 * - `{ 'primaryContact.phone': 1 }` (sparse), `{ 'primaryContact.email': 1 }` (sparse)
 * - `{ 'legal.registrationId': 1 }` (sparse)
 * - `{ roles: 1 }` — "all carriers", "all brokers"
 *
 * See `docs/relations-v2/02-data-model.md` section 8.
 */
export const OrgSchema = createModel({
  status: orgStatusSchema,
  /** Set when `status === 'blocked'`; cleared on activate. Admin-only. */
  blockMeta: blockMetaSchema.optional(),
  /** display name of the Org. */
  name: nameSchema,
  /**
   * Other names this Org is known by — a trade name typed in chat, the
   * name printed on a card. Learned only when the user confirms it or
   * Bayan accepts a number under it; a match here skips the name judge.
   */
  aliases: z.array(nameSchema).optional(),
  /** legal entity (CR, tax id, license). Mirrors `legalEntitySchema` used by Business / Carrier. */
  legal: legalEntitySchema.optional(),
  /** primary contact person for the Org. */
  primaryContact: contactPersonSchema,
  logo: imageWithUploadSchema.optional(),
  /** the currency the Org operates in. */
  currency: currencySchema,
  /** denormalized counts of admins / dispatchers / drivers / employees. */
  members: membersCountSchema,
  /** unified settings; superset of `businessSettingsSchema` and the carrier-side settings. */
  settings: orgSettingsSchema.optional(),
  /** the fleet info of the org. */
  fleet: orgFleetSchema.optional(),
  /**
   * network roles. ≥ 1. An Org may be `customer + carrier + broker`
   * simultaneously. `roles[0]` is the convention for primary-role
   * display.
   */
  roles: z.array(partyRoleSchema).min(1),

  /** denormalized counters for analytics; mirrors Business / Carrier `UTags`. */
  UTags: z.array(UTagSharedSchema).default([]),

  /**
   * Ops / analytics bag (signup estimates + heartbeats). Same role as
   * `User.meta`. Not stripped on reads yet.
   */
  meta: orgMetaSchema.optional(),

  /**
   * Embedded-financing profile filled in by the carrier on their
   * first application (`/financing` apply form). Snapshotted into
   * `FinancingApplication.profileSnapshot` at submit time so the
   * underwriting packet is self-contained. Optional because not
   * every Org applies for financing.
   *
   * See `libs/shared/schema/src/model/financing/financing.profile.model.ts`.
   */
  financingProfile: orgFinancingProfileSchema.optional(),
});

/** `Org db model` — unified replacement for the legacy `Business` and `Carrier` collections. */
export type Org = z.infer<typeof OrgSchema.model>;
export type OrgData = z.infer<typeof OrgSchema.data>;
export type OrgPartial = z.infer<typeof OrgSchema.partial>;
export type OrgQueryMany = QueryMany<Org>;
export type OrgQueryOne = QueryOne<Org>;
